import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ArchiveMode, CASE_STATUSES, Case, CasesQueryParams } from '../../models/case';
import { Partner } from '../../models/partner';
import { PaginatedCollection } from '../../models/PaginatedCollection';
import { CasesService } from '../../../services/cases.service';
import { PartnersService } from '../../../services/partners.service';
import { caseStatusClass, caseStatusLabel, caseTenantId, caseTenantPartners } from '../../utils/case-display';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject, of, debounceTime, takeUntil, switchMap, tap, catchError } from 'rxjs';

/** One request is enough for every tenant we have; revisit with serverSideSearch if that changes. */
const TENANT_OPTIONS_PAGE_SIZE = 200;

@Component({
  selector: 'app-cases',
  templateUrl: './cases.component.html',
  styleUrls: ['./cases.component.scss']
})
export class CasesComponent implements OnInit, OnDestroy {
  cases: Case[] = [];
  error: string | null = null;

  // Only the very first fetch takes over the page. Every later one dims the table in place,
  // so filtering never unmounts the controls the user is typing into.
  initialLoading = true;
  refreshing = false;

  // Pagination
  /** The case awaiting confirmation, or null when the dialog is closed. */
  pendingDelete: Case | null = null;
  deleting = false;

  currentPage = 0;
  pageSize = 10;
  totalElements = 0;
  totalPages = 0;

  // Filter form
  filterForm: FormGroup;

  // Available filter options
  statusOptions = CASE_STATUSES;
  /** Multi-select: the backend ORs the statuses, so an empty set means "any status". */
  selectedStatuses = new Set<string>();

  /** Tenants to filter by. Loaded once — the hub is cross-tenant by default, this narrows it. */
  tenants: Partner[] = [];
  selectedTenantId: number | string | null = null;

  /** Fed only by the search control. The other filters go straight to reload$. */
  private searchInput$ = new Subject<string>();
  private reload$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  /**
   * What the last fetch actually asked for. Replaces distinctUntilChanged, whose internal copy
   * clearFilters() could not keep in step — so clearing and retyping the same term did nothing.
   */
  private lastFetchedTerm = '';

  constructor(
    private casesService: CasesService,
    private partnersService: PartnersService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.filterForm = this.fb.group({
      searchTerm: [''],
      createdAtFrom: [''],
      createdAtTo: [''],
      archiveMode: ['' as ArchiveMode]
    });
  }

  ngOnInit(): void {
    this.loadTenants();

    // Seeded before the streams are wired so the Home tiles ("cases created today") can deep-link
    // into an already-filtered list without costing a second fetch.
    const createdAtFrom = this.route.snapshot.queryParamMap.get('createdAtFrom');
    if (createdAtFrom) {
      this.filterForm.patchValue({ createdAtFrom }, { emitEvent: false });
    }

    this.searchInput$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => {
      if ((searchTerm ?? '').trim() === this.lastFetchedTerm) {
        return;
      }
      this.currentPage = 0;
      this.reload$.next();
    });

    // The other filters are discrete choices — no debounce, fetch straight away.
    ['createdAtFrom', 'createdAtTo', 'archiveMode'].forEach(controlName => {
      this.filterForm.get(controlName)?.valueChanges.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.currentPage = 0;
        this.reload$.next();
      });
    });

    this.filterForm.get('searchTerm')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => this.searchInput$.next(searchTerm));

    this.reload$.pipe(
      tap(() => {
        this.refreshing = true;
        this.error = null;
      }),
      switchMap(() => {
        const filterValues = this.filterForm.value;
        this.lastFetchedTerm = (filterValues.searchTerm ?? '').trim();
        const archiveMode: ArchiveMode = filterValues.archiveMode || '';

        const params: CasesQueryParams = {
          page: this.currentPage,
          size: this.pageSize,
          sort: ['createdAt,desc'], // Sort by creation date descending
          searchTerm: this.lastFetchedTerm || undefined,
          statuses: this.selectedStatuses.size ? Array.from(this.selectedStatuses) : undefined,
          createdAtFrom: filterValues.createdAtFrom || undefined,
          createdAtTo: filterValues.createdAtTo || undefined,
          includeArchived: archiveMode === 'include' || undefined,
          archivedOnly: archiveMode === 'only' || undefined,
          // Absent = every tenant, which is the point of this page.
          tenantId: this.selectedTenantId ?? undefined
        };

        // catchError belongs inside the projection: on the outer pipe the first failure
        // would complete the stream and the filters would go dead for good.
        return this.casesService.getAllCases(params).pipe(
          catchError(error => {
            console.error('Error loading cases:', error);
            this.error = 'Failed to load cases';
            return of(null);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(response => this.applyResponse(response));

    this.reload$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Tenant ids are partner ids, so the tenant dropdown is just the tenant-flagged partners — the
   * same call the Partners page makes.
   */
  private loadTenants(): void {
    this.partnersService.getPartners({
      isTenant: true,
      page: 0,
      size: TENANT_OPTIONS_PAGE_SIZE,
      sort: ['contact.name,asc']
    }).pipe(
      catchError(error => {
        // A missing tenant list must not take the case list down with it.
        console.error('Error loading tenants:', error);
        return of(null);
      }),
      takeUntil(this.destroy$)
    ).subscribe(response => {
      this.tenants = Array.isArray(response?.data) ? response!.data : [];
    });
  }

  private applyResponse(response: PaginatedCollection<Case> | null): void {
    // null means the request failed — keep the rows already on screen.
    if (response) {
      this.cases = Array.isArray(response.data) ? response.data : [];
      this.totalElements = response.total || 0;
      this.totalPages = Math.ceil(this.totalElements / this.pageSize);
    }

    this.initialLoading = false;
    this.refreshing = false;
  }

  /** Kept public: deleteCase() refreshes the list after a successful delete. */
  loadCases(): void {
    this.reload$.next();
  }

  onTenantSelected(tenantId: number | string | null): void {
    this.selectedTenantId = tenantId ?? null;
    this.currentPage = 0;
    this.reload$.next();
  }

  toggleStatus(status: string): void {
    if (this.selectedStatuses.has(status)) {
      this.selectedStatuses.delete(status);
    } else {
      this.selectedStatuses.add(status);
    }
    this.currentPage = 0;
    this.reload$.next();
  }

  isStatusSelected(status: string): boolean {
    return this.selectedStatuses.has(status);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.reload$.next();
  }

  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pageSize = parseInt(target.value, 10);
    this.currentPage = 0;
    this.reload$.next();
  }

  clearFilters(): void {
    // emitEvent: false because a plain reset() fires valueChanges on all the controls, and
    // each of those subscriptions triggers a fetch — one click used to cost four requests.
    this.filterForm.reset(
      { searchTerm: '', createdAtFrom: '', createdAtTo: '', archiveMode: '' },
      { emitEvent: false }
    );
    this.selectedStatuses.clear();
    this.selectedTenantId = null;
    this.currentPage = 0;
    this.reload$.next();
  }

  viewCaseDetails(caseItem: Case): void {
    if (caseItem.id) {
      this.router.navigate(['/cases', caseItem.id]);
    }
  }

  /** Opens the confirmation; the delete itself waits for confirmDelete(). */
  askDelete(caseItem: Case): void {
    if (!caseItem.id) return;

    this.pendingDelete = caseItem;
  }

  confirmDelete(): void {
    const caseItem = this.pendingDelete;

    if (!caseItem?.id) return;

    this.deleting = true;

    // Addressed with the case's own tenant: deleting is a write, and not every backend grants
    // admins the tenant-less bypass.
    this.casesService.deleteCase(caseItem.id, caseTenantId(caseItem)).subscribe({
      next: () => {
        this.deleting = false;
        this.pendingDelete = null;
        this.loadCases();
      },
      error: (error) => {
        console.error('Error deleting case:', error);
        this.deleting = false;
        this.pendingDelete = null;
        // The page already renders `error` as a banner, so this needs no alert().
        this.error = 'Failed to delete the case. It may have been removed already.';
      }
    });
  }

  getPatientName(caseItem: Case): string {
    return caseItem.patient?.name || 'N/A';
  }

  getDoctorName(caseItem: Case): string {
    return caseItem.doctor?.contact?.name || 'N/A';
  }

  getTechName(caseItem: Case): string {
    return caseItem.tech?.contact?.name || 'N/A';
  }

  /** Both sides can be tenants (a dentist tenant and a lab tenant) — showing two is correct. */
  getTenantPartners(caseItem: Case): Partner[] {
    return caseTenantPartners(caseItem);
  }

  getCaseStatusClass(status: string): string {
    return caseStatusClass(status);
  }

  getCaseStatusLabel(status?: string): string {
    return caseStatusLabel(status);
  }

  trackByCaseId(index: number, caseItem: Case): string | number {
    return caseItem.id ?? index;
  }

  trackByPartnerId(index: number, partner: Partner): string | number {
    return partner.id ?? index;
  }

}
